import type { PricingSourceFund } from '../api/client'

// Pure helpers for PricingSourcePanel / PricingSourceAlertBanner (kept out of
// <script setup> so they can be unit-tested — same reason as riskPanelHelpers.ts).

export function ragColour(rag: string): string {
  switch (rag) {
    case 'green': return '#4caf50'
    case 'amber': return '#fbc02d'
    case 'red': return '#f44336'
    default: return '#888'
  }
}

export function ragLabel(rag: string): string {
  return rag === 'green' ? 'Green' : rag === 'amber' ? 'Amber' : rag === 'red' ? 'Red' : '—'
}

export function pct(x: number | null | undefined): string {
  return x === null || x === undefined || Number.isNaN(x) ? '—' : `${Math.round(x * 100)}%`
}

export function signedBps(x: number | null | undefined): string {
  if (x === null || x === undefined || Number.isNaN(x)) return '—'
  const r = Math.round(x)
  return r >= 0 ? `+${r} bp` : `−${Math.abs(r)} bp`
}

export function pickExampleFund(funds: PricingSourceFund[]): PricingSourceFund | null {
  return funds.find(f => f.rag !== 'green') ?? funds[0] ?? null
}

// Charteris Phase 2.5 Task 6: the pricing-source payload's `label` is the FundId
// string (matching useVisibleFunds, same convention as JAPAN's "JAPAN") — but
// CHARTERIS should read as "Silver" (its portfolio label) wherever this panel shows a human label,
// not the all-caps FundId. Deliberately NOT a blanket FUND_REGISTRY lookup for
// every fund (that would also rename JAPAN's long-standing "JAPAN" display,
// which is out of scope here) — just the one new entry.
// AMATI reads as "Copper" the same way (2026-10-13).
const FUND_DISPLAY_LABELS: Record<string, string> = { CHARTERIS: 'Silver', AMATI: 'Copper', MINERS: 'Miners', JAPANESG: 'Japan ESG', TECH: 'Tech', ELECTRUM: 'Electrum',
  JHTECH: 'JHTech', ASIATECH: 'AsiaTech', BARINGSOIL: 'BaringsOil', SPWORLD: 'SPWorld', SMALLCAP: 'SmallCap' }

export function fundDisplayLabel(label: string): string {
  return FUND_DISPLAY_LABELS[label] ?? label
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Fixed month names: toLocaleDateString('en-GB') prints "Sept" on newer ICU builds.
function shortDate(iso: string): string {
  const [, m, d] = iso.split('-').map(Number)
  return `${d} ${MONTHS[m - 1]}`
}

// Δm (moveBps) is NOT the strike-to-strike futures move: it is the futures' move
// since the last US close measured at this strike, minus the same measure at the
// previous strike — the part of the futures move not yet in the US closing prices.
export function exampleSentence(f: PricingSourceFund): string | null {
  const s = f.sample
  if (!s) return null
  const day = s.dayRatio === null ? '' : ` — ${pct(s.dayRatio)} that day`
  const scale = f.betaScale && f.betaScale !== 1
    ? ` (β-scaled: the fund runs at ${f.betaScale.toFixed(2)}× ${f.proxy}, so the fitted slope is divided by `
      + `${f.betaScale.toFixed(2)})`
    : ''
  return `On ${shortDate(s.date)} the ${f.proxy} futures had moved ${signedBps(s.moveBps)} more since the last `
    + `US close than at the ${shortDate(s.prevDate)} strike (${f.valuationClock}) — a move not yet in the US `
    + `closing prices. The close-based synthetic said ${signedBps(s.closeModelBps)}; the official NAV moved `
    + `${signedBps(s.navBps)}, so ${signedBps(s.residualBps)} of that move was already priced in${day}. Over the `
    + `last 40 paired NAV days the fitted pass-through is ${pct(f.passThrough40)}${scale}, leaving `
    + `${pct(f.edgeLeft40)} of each such move for the next strike — that remainder is the strategy's edge.`
}
